import { memo, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Drawer, useMediaQuery } from "@mui/material";
import { designTokens } from "@design-system";

import DrawerHeader from "./DrawerHeader";
import DrawerContent from "./DrawerContent";
import MiniDrawerStyled from "./MiniDrawerStyled";
import { drawerWidth } from "@/AppConfig";

interface MainDrawerProps {
  open: boolean;
  handleDrawerToggle: () => void;
  window?: () => Window;
}

const MainDrawer = memo<MainDrawerProps>(function MainDrawer({ open, handleDrawerToggle, window }) {
  const theme = useTheme();
  const matchDownMD = useMediaQuery(theme.breakpoints.down("lg"));

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const drawerContent = useMemo(() => <DrawerContent />, []);
  const drawerHeader = useMemo(() => <DrawerHeader open={open} />, [open]);

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { md: 0 }, zIndex: 1300 }}
      aria-label="mailbox folders"
    >
      {!matchDownMD ? (
        <MiniDrawerStyled variant="permanent" open={open}>
          {drawerHeader}
          {drawerContent}
        </MiniDrawerStyled>
      ) : (
        <Drawer
          container={container}
          variant="temporary"
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", lg: "none" },
            // closed: the (kept-mounted) modal layer must never catch clicks. Its "exited" state was seen
            // stuck after closing, leaving an invisible full-screen layer over the page (page "froze").
            ...(open ? {} : { pointerEvents: "none" }),
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "none",
              backgroundImage: "none",
              background: `linear-gradient(180deg, ${designTokens.colors.background.dark} 0%, ${designTokens.colors.background.darkPaper} 50%, ${designTokens.colors.background.dark} 100%)`,
              boxShadow: designTokens.shadows.lg,
            },
          }}
        >
          {open && drawerHeader}
          {open && drawerContent}
        </Drawer>
      )}
      
    </Box>
  );
});

MainDrawer.displayName = 'MainDrawer';

export default MainDrawer;
